import { Dialog, colorValue } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { isNil } from 'ramda';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/js/component/actionBar/actionBar';
import { NoContentAvailable } from 'src/js/component/no-content-available';
import { SortType } from 'src/js/component/sort-manager';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import streamModels, { deleteStream, fetchStreams, startStopStream } from 'src/js/pages/streaming/stream/stream-store';
import { StreamList } from 'src/js/pages/streaming/stream/stream-list';
import { StreamViewModel, streamIsStarted, streamIsStopped } from 'src/js/pages/streaming/stream/stream-models';
import { StreamingActionBar, scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/util/constant';
import { isNilOrEmpty } from 'src/js/util/global-util';

import { fetchAudioEncoders } from '../audio-encoder/audio-encoder-store';
import { fetchMetadata } from '../metadata/metadata-store';
import { fetchScte } from '../scte/scte-store';
import { fetchVideoEncoders } from '../video-encoder/video-encoder-store';
import { AddStream, StreamAddFormRef } from './add-stream-modal';
import { StreamDeleteConfirmModal } from './delete-confirm-modal';
import { createNewStreamViewModel, streamModelToStatus } from './stream-view-model-mapper';
import { useLocalStorage } from 'usehooks-ts';

let connectionLost = false;
export const StreamsPage: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();
  const newStreamModel = createNewStreamViewModel();
  const addStreamRef = useRef<StreamAddFormRef>();

  // state management BEGINs
  const [streams, setStreams] = useState(streamModels);
  const [initialized, setInitialized] = useState(false);
  const [deleteShown, showDelete, hideDelete] = useModalControls();
  const [addModalShown, showAddModal, hideAddModal] = useModalControls();
  const [addButtonState, setAddButtonState] = useState<ButtonStateType>(undefined);

  const updateStreamList = (updates: StreamViewModel[]) => {
    setStreams((existing) => {
      return updates.map((s) => {
        const item = existing.find((i) => i.id == s.id); // TODO we need some uuid in a private field for deletable objects like streams.
        if (item) {
          s.selected = item.selected;
          s.hidden = item.hidden;
        }
        return s;
      });
    });
  };

  useWatchObject(streamModels, updateStreamList);

  useAdaptiveFetch(() => fetchVideoEncoders(t), constant.defaultRefreshInterval * 3);
  useAdaptiveFetch(() => fetchAudioEncoders(t), constant.defaultRefreshInterval * 3);
  useAdaptiveFetch(() => fetchMetadata(t), constant.defaultRefreshInterval * 6);
  useAdaptiveFetch(() => fetchScte(t), constant.defaultRefreshInterval * 6);

  useAdaptiveFetch(
    () =>
      fetchStreams(t).then((res) => {
        if (res.ok) {
          setInitialized(true);
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    constant.defaultRefreshInterval,
  );

  const fetchData = () => {
    fetchVideoEncoders(t);
    fetchAudioEncoders(t);
    fetchMetadata(t);
    fetchScte(t);
  };

  watchEvent('preset-load', () => {
    fetchStreams(t);
    fetchData();
  });
  // state management ENDs

  const statusTypes: Status[] = [
    { status: 'STREAMING', color: colorValue('haiui-green-01') },
    { status: 'LISTENING', color: colorValue('haiui-blue-04') },
    { status: 'ALERT', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const filteredStart = () => streams.filter((e) => e.selected && streamIsStopped(e.stats?.state));
  const handleStartEncoder = () => {
    const filtered = filteredStart();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopStream(e, true, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('stream.notifications.startError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => streams.filter((e) => e.selected && streamIsStarted(e.stats?.state));
  const handleStopEncoder = () => {
    const filtered = filteredStop();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopStream(e, false, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('stream.notifications.stopError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredDelete = () => streams.filter((e) => e.selected);
  const handleDeleteStreams = () => {
    const filtered = filteredDelete();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => deleteStream(e))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 0) {
          createAndDispatchNotification(
            t('stream.notifications.deleteError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const handleSelect = (item: StreamViewModel, selected: boolean) => {
    const models = streams.map((i) => (i.id === item.id ? { ...i, selected } : i));
    setStreams(models);
  };

  const onSelectAll = (selected: boolean) => {
    const models = streams.map((i) => ({ ...i, selected }));
    setStreams(models);
  };

  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      const button = document.getElementById('stream-add-form-submit');
      if (!isNilOrEmpty(button)) {
        e.preventDefault();
        button.click();
      }
    }
  };

  const handleAddStream = () => {
    setAddButtonState('pending');
    addStreamRef.current.submitStream().then((res: any) => {
      setAddButtonState(undefined);
      if (res?.ok) {
        const response = res.data;
        hideAddModal();
        scrollPanelIntoView({ id: response.info.id }, true);
      }
    });
  };

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:essence`,
    [],
  );

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
    destination: { title: t('sort.sortText.destination'), type: SortType.NATURAL_SORT },
    encapsulationTextual: { title: t('sort.sortText.encapsulation'), type: SortType.NATURAL_SORT }, // aka Protocol "TS over UDP"
    stateSortable: { title: t('sort.sortText.status'), type: SortType.GENERIC },
  };

  return (
    <>
      {initialized && (isNil(streams) || streams.length === 0) ? (
        <NoContentAvailable
          title={t('stream.noContent.title')}
          subText={t('stream.noContent.subText')}
          btnText={t('stream.noContent.btnText')}
          iconName="Streams"
          onClick={showAddModal}
          btnDisabled={sessionStore.isUser()}
        />
      ) : (
        <StreamingActionBar
          items={streams}
          showPlaceholder={false}
          searchKeys={[
            'name',
            'encapsulationTextual',
            'destination',
            'videoOutputResolution',
            'videoBitrate',
            'audioBitrate',
            'codecAlgorithm',
          ]}
          initialSearchFilters={searchFilters}
          onChangeSearchFilters={setSearchFilters}
          sortInfo={sortInfo}
          statusTypes={statusTypes}
          getStateValue={streamModelToStatus}
          actionButtons={
            sessionStore.isUser()
              ? []
              : [
                  {
                    onClick: handleStartEncoder,
                    counter: filteredStart,
                    label: t('general.start'),
                  },
                  { onClick: handleStopEncoder, counter: filteredStop, label: t('general.stop') },
                  { onClick: showDelete, counter: filteredDelete, label: t('general.deleteBtn') },
                ]
          }
          List={StreamList}
          onSelectAll={onSelectAll}
          updateSelection={handleSelect}
          storageKey="streams"
          actionRightPrimary={
            sessionStore.isUser()
              ? null
              : {
                  onClick: showAddModal,
                  children: t('stream.button.addStream'),
                }
          }
          placeholderThumbnail={true}
          placeholderToggles={4}
          placeholderRightComponents={1}
        />
      )}
      {deleteShown && (
        <StreamDeleteConfirmModal
          itemsToDelete={filteredDelete()}
          onCancel={hideDelete}
          onApply={handleDeleteStreams}
        />
      )}
      {addModalShown && (
        <Dialog
          title={t('stream.addStream')}
          dialogType="activity"
          headerIcon="StreamsOut"
          accentColor={constant.productColor}
          size="lg"
          className="mk-three-columns"
          content={<AddStream model={newStreamModel} ref={addStreamRef} />}
          onClose={hideAddModal}
          onKeyUp={handleOnKeyUp}
          buttons={[
            { variant: 'secondary', onClick: hideAddModal, label: t('general.cancel') },
            {
              id: 'stream-add-form-submit',
              variant: 'primary',
              state: addButtonState,
              close: false,
              onClick: handleAddStream,
              label: t('stream.button.addStream'),
            },
          ]}
        />
      )}
    </>
  );
};
